// src/components/Services/Services.jsx

import React, { useEffect, useState, Fragment } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchServices } from "../../redux/actions/services";
import {
  Box,
  Skeleton,
  Button,
  Typography,
  styled,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "@mui/material";
import TestimonialSection from "../Testimonials/TestimonilaSection";
import styleImg from "../../assets/images/style2.jpg";

const ImageContainer = styled(Box)({
  position: "relative",
  width: "100%",
  overflow: "hidden",
  "& img": {
    width: "100%",
    height: "auto",
    objectFit: "cover",
  },
});

const Services = () => {
  const dispatch = useDispatch();
  const { services, loading, error } = useSelector((s) => s.servicesState);
  const [loadedImages, setLoadedImages] = useState({});

  useEffect(() => {
    dispatch(fetchServices());
  }, [dispatch]);

  useEffect(() => {
    const flags = (services || [])
      .filter((svc) => svc.image_url)
      .reduce((acc, svc) => ({ ...acc, [svc.id]: false }), {});
    setLoadedImages(flags);
  }, [services]);

  const handleImageLoad = (id) =>
    setLoadedImages((prev) => ({ ...prev, [id]: true }));

  if (loading) return <Typography>Loading services…</Typography>;
  if (error) return <Typography color="error">{error}</Typography>;

  const displayOrder = [4, 7, 5, 6];
  // map each ID to its service object (drops any missing ones)
  const orderedServices = displayOrder
    .map((id) => services.find((svc) => svc.id === id))
    .filter(Boolean);

  return (
    <>
      <div className="services-container">
        <div className="services-inner" style={{ maxWidth: "1440px" }}>
          {/* Header */}
          <div className="services-header">
            <Typography variant="h4">SERVICES</Typography>
          </div>

          {/* Services & beautiful CTA */}
          {orderedServices.map((svc, idx) => (
            <Fragment key={svc.id}>
              {/* ——— Service Card ——— */}
              <Box
                className="services-pricing"
                sx={{
                  mb: { xs: 4, md: 8 } /* 32px on xs, 64px on md */,
                }}
              >
                <div className="left">
                  <Typography variant="h5">{svc.title}</Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      whiteSpace: "pre-line",
                      wordBreak: "normal",
                      overflowWrap: "normal",
                      hyphens: "none",
                      textAlign: "left",
                      pr: { xs: 0, sm: 2 },
                    }}
                  >
                    {svc.description}
                  </Typography>
                  <Table size="small" sx={{ mt: 2 }}>
                    <TableBody>
                      {svc.packages.map((pkg) => (
                        <TableRow key={pkg.id} hover>
                          <TableCell component="th" scope="row">
                            {pkg.name}
                          </TableCell>
                          <TableCell
                            align="right"
                            style={{ color: "transparent" }}
                          >
                            {pkg.price} JD
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>

                  <Box sx={{ mt: 2 }}>
                    <Button
                      component="a"
                      href={`mailto:Haifaakhalidofficial@yahoo.com?subject=${encodeURIComponent(
                        svc.title,
                      )}`}
                      variant="contained"
                      sx={{
                        backgroundColor: "black",
                        color: "white",
                        textTransform: "none",
                        px: 3,
                        py: 1.5,
                        "&:hover": {
                          backgroundColor: "white",
                          color: "black",
                          border: "1px solid black",
                        },
                      }}
                    >
                      For Inquiry
                    </Button>
                  </Box>
                </div>
                {svc.image_url && (
                  <div className="right">
                    <ImageContainer>
                      <img
                        src={svc.image_url}
                        alt={svc.title}
                        onLoad={() => handleImageLoad(svc.id)}
                        style={{
                          display: loadedImages[svc.id] ? "block" : "none",
                        }}
                      />
                      {!loadedImages[svc.id] && (
                        <Skeleton
                          variant="rectangular"
                          width="100%"
                          height="100%"
                        />
                      )}
                    </ImageContainer>
                  </div>
                )}
              </Box>

              {/* ——— New Airy CTA after the first service ——— */}
              {idx === 0 && (
                <Box
                  component="section"
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                    gap: { xs: 4, md: 8 },
                    alignItems: "center",
                    my: { xs: 6, md: 10 },
                    px: { xs: 3, md: 6 },
                  }}
                >
                  <Box
                    component="img"
                    src={styleImg}
                    alt="logo FASHION STYLIST | CREATIVE DIRECTOR"
                    sx={{
                      width: { xs: "80%", md: "50%" },
                      maxWidth: 400,
                      borderRadius: 3,
                      boxShadow: 3,
                      justifySelf: "center",
                    }}
                  />

                  <Box sx={{ textAlign: { xs: "center", md: "left" } }}>
                    <Typography variant="h4" sx={{ mb: 1 }}>
                      Free styling consultation session
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 3, color: "#444" }}>
                      Wondering what a styling session entails? <br /> Book in
                      your free 15 minute to know more!
                    </Typography>
                    <button
                      className="cta"
                      onClick={() =>
                        (window.location.href = `mailto:Haifaakhalidofficial@yahoo.com?subject=${encodeURIComponent(
                          "free 15 min style consultation",
                        )}`)
                      }
                    >
                      <span className="hover-underline-animation">
                        Book Now
                      </span>
                      <svg
                        id="arrow-horizontal"
                        xmlns="http://www.w3.org/2000/svg"
                        width="30"
                        height="10"
                        viewBox="0 0 46 16"
                      >
                        <path
                          d="M8,0,6.545,1.455l5.506,5.506H-30V9.039H12.052L6.545,14.545,8,16l8-8Z"
                          transform="translate(30)"
                          fill="currentColor"
                        />
                      </svg>
                    </button>
                  </Box>
                </Box>
              )}
            </Fragment>
          ))}
        </div>
      </div>

      <TestimonialSection />
    </>
  );
};

export default Services;
